/// <reference path="../../../../WebUI/typings/tsd.d.ts" />
import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Rest from "../../Generated/RestClient";
import {Treebeard, TreebeardProps, TreeNode, decorators} from "react-treebeard";
import AceEditor from "react-ace";
import * as csharp from "brace/mode/csharp";
import * as javascript from "brace/mode/javascript";
import * as xml from "brace/mode/xml";
import * as json from "brace/mode/json";
import * as typescript from "brace/mode/typescript";
import * as theme from "brace/theme/github";
import {getExt} from "../../../../Intercom.ReactUI/Utils/Path";
import treeStyle from "../../Views/TreeStyle"
import * as pinBottom from  "../../../../Intercom.ReactUI/Views/PinToBottom";
import {BladeType} from "../Services/azurePortalLinks";
import {BotTab} from "../../Views/BotTab";
import {LogStream, LogStreamProps, LogStreamState} from "../../Views/LogStream";
import isInFrame from "../../../../Intercom.ReactUI/Utils/InFrame";
import {renderIconForScmType, toggleContinuousIntegrationSettings} from "./BotAppContinuousIntegration";
import {debugAllTabs, BotPageTab} from "../../Views/BotTabPage";
import {ProgressRingControl, TimestampControl} from "../../../../Intercom.ReactUI/Views/index";

export interface BotCodeEditorProps extends React.Props<BotCodeEditor> {
    botId: string,
    botEndpoint: string,
    onToggle(node: TreeNode, toggled: boolean),
    onSave(url: string, content: string),
    onSetBotId(botId: string),
    onAddFile();
    onRefresh();
    onRenameFile(newName: string);
    onChangeCode(txt: string, dirty: boolean);
    clearLogStream();
    openBlade(bladeType: BladeType);
    openTab(tabType: BotPageTab, callback?: Function);
    scmType: string | (() => string);
    onSetCodeEditor(x: BotCodeEditor): void;
}

export interface BotCodeEditorState {
    root: TreeNode;
    activeNode: TreeNode;
    activeFile: TreeNode;
    activeCode: string;
    activeCodeDirty: boolean;
    logStream: string;
    botDeploymentLog?: Rest.VfsNodeInfo;
    botDeploymentLogFound?: boolean;
    newFileNodeId?: string;
    lastFetchTime?: Date;
}

export function mapFilesToTreeNodes(files: Rest.VfsNodeInfo[]): TreeNode[] {
    return _.map(files, file => {
        return {
            id: file.href,
            name: file.name,
            children: file.href[file.href.length - 1] == '/' ? [] : undefined
        }
    });
}

export enum SourceTypes {
    csharp,
    javascript,
    typescript,
    json,
    xml
}

const extToSourceTypes = {
    '.js': SourceTypes.javascript,
    '.csx': SourceTypes.csharp,
    '.xml': SourceTypes.xml,
    '.ts': SourceTypes.typescript,
    '.json': SourceTypes.json,
    '.xproj': SourceTypes.xml
};

const modules = [csharp, javascript, typescript, json, xml, theme];

class CustomContainer extends decorators.Container {
    constructor(props: React.Props<any>) {
        super(props);
    }

    public render() {
        const {style, decorators, terminal, onClick, node} = this.props;
        let containerStyle = style.container;
        if (_.isArray(containerStyle)) {
            let args = _.clone(containerStyle);
            args.unshift({});
            containerStyle = _.extend.apply(null, args);
        }
        return (
            <div
                ref="clickable"
                onClick={onClick}
                style={containerStyle}>
                { !terminal ? this.renderToggle() : null }
                <decorators.Header
                    node={node}
                    style={style.header}
                    terminal={terminal}
                    />
            </div>
        );
    }

    public renderToggleDecorator() {
        let decorators = this.props.decorators;
        let style = this.props.style;
        let node = this.props.node;
        return (<decorators.Toggle style={style['toggle']} node={node}/>);
    }
}

// TODO: move to intercom.ReactUI for code sharing
const KeyCodes = {
    enter: 13
}

export class BotCodeEditor extends React.Component<BotCodeEditorProps, BotCodeEditorState>
    implements BotTab {
    public code: string;
    public codeDirty: boolean;
    private saveButton: HTMLButtonElement;
    private logStream: LogStream;
    private fileNameEditor: HTMLInputElement;
    private aceEditor: AceEditor;
    private codeExplorer: HTMLDivElement;
    private codeExplorerListener: EventListener;
    private codePanel: HTMLDivElement;
    private codePanelListener: EventListener;
    private pendingResetUndoStack: boolean;

    constructor(props: BotCodeEditorProps) {
        super(props);
        this.state = {
            root: {
                id: this.props.botId,
                name: this.props.botId,
                children: [],
                toggled: false,
                loading: true
            },
            activeNode: null,
            activeFile: null,
            activeCode: "",
            activeCodeDirty: false,
            logStream: "",
            botDeploymentLogFound: false
        };
    }

    private getMode(): string {
        let sourceType = SourceTypes.csharp;
        if (this.state.activeFile) {
            let curType = extToSourceTypes[getExt(this.state.activeFile.name).toLowerCase()];
            if (!_.isUndefined(curType)) {
                sourceType = curType;
            }
        }
        return SourceTypes[sourceType];
    }

    private get scmType(): string {
        if (this.props.scmType instanceof Function) {
            return this.props.scmType();
        } else {
            return this.props.scmType;
        }
    }

    private isBoundToSourceControl() {
        return !((this.scmType || 'none').toLowerCase() === 'none');
    }

    private getToggleClass(props: any) {
        return `tree-toggle ${props.node && props.node.toggled ? 'expand' : ''}`;
    }

    public setBotId(botId: string) {
        this.props.onSetBotId(botId);
    }

    public setBot(bot: Rest.BotService) {
        // do nothing
    }

    public componentDidMount() {
        $(this.fileNameEditor).keypress((e) => {
            if (e.keyCode == KeyCodes.enter) {
                $(e.currentTarget).blur();
            }
        });

        if (this.codeExplorer) {
            this.codeExplorerListener = pinBottom.bind($(this.codeExplorer), $('.outline.footer'), $('#app-body'));
        }
        if (this.codePanel) {
            this.codePanelListener = pinBottom.bind($(this.codePanel), $('.outline.footer'), $('#app-body'),  () => {
                if (this.aceEditor && this.aceEditor.editor) {
                    this.aceEditor.editor.resize();
                }
            });
        }

        if (this.aceEditor && this.aceEditor.editor && this.aceEditor.editor.session) {
            this.aceEditor.editor.session.setUseWorker(false);
        }
    }

    public componentWillUnmount() {
        $(this.fileNameEditor).off('keypress');
        if (this.codeExplorerListener) {
            pinBottom.unbind(this.codeExplorerListener);
            this.codeExplorerListener = null;
        }
        if (this.codePanelListener) {
            pinBottom.unbind(this.codePanelListener);
            this.codePanelListener = null;
        }
        // remember the last cursor position
        let curNode = this.state.activeNode;
        if (this.aceEditor && this.aceEditor.editor) {
            let curPos = this.aceEditor.editor.getCursorPosition();
            if (curNode && curPos) {
                curNode['cursorPos'] = curPos;
            }
        }
    }

    public componentDidUpdate(prevProps: BotCodeEditorProps, prevState: BotCodeEditorState) {
        const $fileNameEditor = $(this.fileNameEditor);
        if ($fileNameEditor.hasClass('open')) {
            const $explorer = $fileNameEditor.parent();
            const $newFileNode = $explorer.find('.create-file-name');
            if ($newFileNode.length) {
                // now position $fileNameEditor on top of $newFileNode
                const box = $explorer[0].getBoundingClientRect();
                const nodeBox = $newFileNode[0].getBoundingClientRect();
                $fileNameEditor.css({
                    'position': 'absolute',
                    'top': `${nodeBox.top - box.top}px`,
                    'left': `${nodeBox.left - box.left}px`
                }).focus();
            }
        }

        if (this.pendingResetUndoStack) {
            if (this.aceEditor && this.aceEditor.editor) {
                if (this.aceEditor.editor.getValue() == this.state.activeCode) {
                    let undoMgr = this.aceEditor.editor.session.getUndoManager();
                    if (undoMgr) {
                        undoMgr.reset();
                        this.pendingResetUndoStack = false;
                    }
                }
            }
        }

        this.gotoPreviousCursor();

        this.aceEditor.editor.setOption('readOnly', this.isBoundToSourceControl());
    }

    private openContinuousSettings() {
        toggleContinuousIntegrationSettings($('section.bot-app-setting a.expando.closed'));
    }

    private onSaveCurrentFile() {
        if (!this.isBoundToSourceControl() && this.state.activeCodeDirty) {
            this.props.onSave(this.state.activeFile.id, this.code);
        }
    }

    private gotoPreviousCursor() {
        let pos = this.state.activeNode ? this.state.activeNode['cursorPos'] : null;
        // check if we have the matching code loaded
        let lines = this.aceEditor && this.aceEditor.editor && this.aceEditor.editor.session ?
            this.aceEditor.editor.session.getLength() : -1;
        if (pos && this.aceEditor && this.aceEditor.editor && pos.row + 1 <= lines) {
            this.aceEditor.editor.gotoLine(pos.row + 1, pos.column);
            delete this.state.activeNode['cursorPos'];
        }
    }

    public resetUndoStack() {
        this.pendingResetUndoStack = true;
    }

    public getCursorPosition(): Object {
        return this.aceEditor && this.aceEditor.editor ? this.aceEditor.editor.getCursorPosition() : 0;
    }

    private showLoading(): JSX.Element {
        return <ProgressRingControl
                onMounted={ div => $(div).closest("ul").toggleClass("code-explorer-loading", true) }
                onUnmount={ div => $(div).closest('ul.code-explorer-loading').toggleClass('code-explorer-loading', false) }
                />;
    }

    public render(): JSX.Element {
        const showAddButton = false;

        var isBoundToSourceControl = this.isBoundToSourceControl();

        var message: JSX.Element = isBoundToSourceControl ? <div className="app-message">Continuous deployment connected, read only view</div> : null;

        let lastDeployTime = this.state.botDeploymentLog ? new Date(this.state.botDeploymentLog.mtime as any) : null;
        let showRefresh = lastDeployTime && this.state.lastFetchTime && this.state.lastFetchTime.getTime() < lastDeployTime.getTime();
        if (isInFrame() || debugAllTabs) {
            if (isBoundToSourceControl) {
                message = <div className="app-message source-repo">
                    {renderIconForScmType(this.scmType) }
                    {showRefresh ? <TimestampControl time={new Date(this.state.botDeploymentLog.mtime as any)}/> : null}
                    {showRefresh ? <a onClick={() => this.props.onRefresh() }>Refresh</a> : null}
                    <a onClick={x => this.props.openTab(BotPageTab.settings, () => this.openContinuousSettings()) }> Edit continuous deployment</a>
                </div>;
            } else {
                message = <div className="app-message"><a onClick={x => this.props.openTab(BotPageTab.settings, () => this.openContinuousSettings()) }>Configure continuous deployment</a> - manage your code in your repo of choice and edit locally.</div>
            }
        }

        return (
            <div>
                <div className="code-toolbar">
                    { message }
                    {/*
                    <button type="button" onClick={() => this.props.onAddFile() }
                        style={{ visibility: showAddButton ? 'visible' : 'hidden' }}
                        disabled={isBoundToSourceControl || !(this.state.activeNode && this.state.root && this.state.root.children && this.state.root.children.length) }>
                        Add
                    </button>
                    */
                    }
                    { isBoundToSourceControl ? null :
                        <button
                            ref={ x => { this.saveButton = x } }
                            className="default-button"
                            disabled={isBoundToSourceControl || !this.state.activeCodeDirty}
                            onClick={() => this.props.onSave(this.state.activeFile.id, this.code) }>Save</button>
                    }
                </div>
                <div className="code-tab">
                    <div className="code-explorer" ref={x => this.codeExplorer = x}>
                        <Treebeard key="code-explorer-tree" data={this.state.root} onToggle={this.props.onToggle}
                            style={treeStyle}
                            decorators={$.extend(true, decorators, {
                                Loading: _.bind(this.showLoading, this),
                                Container: CustomContainer,
                                Header: (props: any) => {
                                    const style = props.style;
                                    const isNewFile = props.node.newFile;
                                    const isFile = props.terminal;
                                    let className = isNewFile ? 'create-file-name' : (isFile ? 'leaf-file' : '');
                                    className = `${className} treenode`;
                                    const nodeId = isNewFile ? props.node.id : null;
                                    let fileName = (
                                        <div style={style.style}
                                            data-nodeId={nodeId}
                                            className={className}>{props.node.name}
                                        </div>);
                                    return (
                                        <div style={style.base}>
                                            {fileName}
                                        </div>);
                                },
                                Toggle: (props) => {
                                    const style = props.style;
                                    return (
                                        <div style={style.base} className={this.getToggleClass(props) }>
                                        </div>
                                    );
                                }
                            }) }
                            animations={false}/>
                        <input ref={x => this.fileNameEditor = x}
                            className={this.state.newFileNodeId ? 'file-name-editor open' : 'file-name-editor'}
                            placeholder="filename.ext"
                            onBlur={e => {
                                this.props.onRenameFile(e.target['value']);
                                this.fileNameEditor.value = '';
                            } }
                            />
                    </div>
                    <div className="code-panel" ref={x => this.codePanel = x}>
                        <div className="code-editor">
                            <AceEditor
                                name="codeEditor"
                                mode={this.getMode() }
                                theme='github'
                                width="100%"
                                height="100%"
                                showPrintMargin={false}
                                commands={[{
                                    name: 'saveFile',
                                    bindKey: {
                                        win: 'Ctrl-S',
                                        mac: 'Command-S',
                                        sender: 'editor|cli',
                                    },
                                    exec: (env, args, req) => {
                                        this.onSaveCurrentFile();
                                    }
                                }]}
                                onChange={txt => {
                                    this.code = txt;
                                    this.props.onChangeCode(txt, true);
                                } }
                                value={this.state.activeCode}
                                editorProps={{ $blockScrolling: true }}
                                ref={x => this.aceEditor = x }
                                />
                        </div>
                        <div className="logger-panel">
                            <div className="logger-head">
                                <label>Log</label>
                                <label className="log-stream-clear" onClick={this.props.clearLogStream}>Clear</label>
                            </div>
                            <LogStream ref={x => { this.logStream = x } } logs={this.state.logStream}/>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./Client/v4/Views/BotCodeEditor.tsx